import { useState } from 'react';
import { MoreHoriz, Settings, Sparks, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import type { IFolder } from 'features/chat/agent-chat-module/types/kb.service.type';
import { useDeleteFolder } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { AddFolderToAgentDialog } from './add-folder-to-agent-dialog';
import { ConfigureFolderModal } from './configure-folder-modal';
import { cn } from 'lib/utils';
import { getApiErrorDetail } from 'utils/api-error';

type FolderCardDropdownProps = {
  projectKey: string;
  folder: IFolder;
};

export function FolderCardDropdown({ projectKey, folder }: FolderCardDropdownProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const canDelete = useIsOwner(folder);
  const { mutateAsync: deleteFolder, isPending } = useDeleteFolder();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [isAddToAgentOpen, setIsAddToAgentOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteFolder({
        folder_id: folder._id,
        project_key: projectKey,
      });

      const folderName = folder.Name || t('UNTITLED_FOLDER', { defaultValue: 'Untitled folder' });
      toast({
        variant: 'success',
        title: t('FOLDER_DELETED', { defaultValue: 'Folder deleted' }),
        description: t('FOLDER_DELETED_SUCCESSFULLY', {
          name: folderName,
          defaultValue: `${folderName} was deleted successfully.`,
        }),
      });
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast({
        title: t('FAILED_TO_DELETE_FOLDER', { defaultValue: 'Failed to delete folder' }),
        description:
          getApiErrorDetail(error) ?? t('PLEASE_TRY_AGAIN', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    }
  };

  const menuLabel = t('MORE_OPTIONS', { defaultValue: 'More options' });

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={menuLabel}
                  className={cn(
                    'h-6 w-6 rounded opacity-100 hover:bg-grey-50 focus-visible:ring-0 focus-visible:ring-offset-0 dark:hover:bg-white/10',
                    theme === 'dark' ? 'text-true-white/70' : 'text-grey-700 hover:text-grey-700'
                  )}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <MoreHoriz width={20} height={20} strokeWidth={1.75} />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent side="top">{menuLabel}</TooltipContent>
            <DropdownMenuContent align="start" side="right" sideOffset={6} className="min-w-48">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsMenuOpen(false);
              setIsConfigureModalOpen(true);
            }}
          >
            <Settings width={20} height={20} strokeWidth={1.5} />
            {t('CONFIGURE') || 'Configure'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsMenuOpen(false);
              setIsAddToAgentOpen(true);
            }}
          >
            <Sparks width={20} height={20} strokeWidth={1.5} />
            {t('ADD_TO_AGENT') || 'Add to agent'}
          </DropdownMenuItem>
          {canDelete ? (
            <DropdownMenuItem
              className={dropdownMenuItemDestructiveClass}
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
            >
              <Trash width={20} height={20} strokeWidth={1.5} />
              {t('DELETE') || 'Delete'}
            </DropdownMenuItem>
          ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>

      <ConfigureFolderModal
        projectKey={projectKey}
        folder={folder}
        open={isConfigureModalOpen}
        onOpenChange={setIsConfigureModalOpen}
      />

      <AddFolderToAgentDialog
        open={isAddToAgentOpen}
        onOpenChange={setIsAddToAgentOpen}
        projectKey={projectKey}
        folderId={folder._id}
      />

      <ConfirmationModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title={t('DELETE_FOLDER') || 'Delete folder'}
        description={
          t('DELETE_FOLDER_CONFIRMATION_DESCRIPTION', {
            name: folder.Name || t('THIS_FOLDER') || 'this folder',
          }) ||
          `Are you sure you want to delete ${folder.Name || 'this folder'}? This action cannot be undone.`
        }
        onConfirm={handleDelete}
        confirmText={t('DELETE') || 'Delete'}
        cancelText={t('CANCEL') || 'Cancel'}
        preventAutoClose
        isLoading={isPending}
        confirmVariant="delete"
      />
    </>
  );
}
